import { FC, memo, ReactElement, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { AppRoutes } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { messageOnError } from '@/common/utils';

import { SearchConsoleAccountsQueryKeys, useDeleteSearchConsoleAccountsMutation, useGetByIdSearchConsoleAccountsQuery, useRevokeSearchConsoleAccountsTokenOAuth2Mutation } from '@/modules/DomainsApi';

import { SearchConsoleAccountsOverviewView } from './SearchConsoleAccountsOverviewView';

interface Props {
  searchConsoleAccountId: string;
}

export const SearchConsoleAccountsOverviewContainer: FC<Props> = memo(({ searchConsoleAccountId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();

  const {
    data: searchConsoleAccountData,
    error: searchConsoleAccountError,
    isFetching: isSearchConsoleAccountFetching,
    isError: isSearchConsoleAccountError,
    refetch: refetchSearchConsoleAccount
  } = useGetByIdSearchConsoleAccountsQuery(searchConsoleAccountId)({
    enabled: !!searchConsoleAccountId,
    onSuccess: (data) => {
      if (!data.clientId && !data.clientSecret) {
        navigate(
          AppRoutes.DomainsApiSearchConsoleAccountsCreateCreds.replace(':searchConsoleAccountId', data.id),
          { replace: true, state: { shouldAlert: true } }
        );
      } else if (!data.isRefreshTokenSetted) {
        navigate(
          AppRoutes.DomainsApiSearchConsoleAccountsCreateOauth2.replace(':searchConsoleAccountId', data.id),
          { replace: true, state: { shouldAlert: true } }
        );
      }
    }
  });

  const { isLoading: isDeleteSearchConsoleAccountLoading, mutate: deleteSearchConsoleAccountMutation } =
    useDeleteSearchConsoleAccountsMutation(searchConsoleAccountId)({
      onSuccess: async () => {
        appMessageApi.success('Search Console акаунт успішно видалено!');

        await queryClient.refetchQueries([SearchConsoleAccountsQueryKeys.GetAll]);

        navigate(AppRoutes.DomainsApiSearchConsoleAccounts);
      },
      onError: (error) => {
        messageOnError(error, appMessageApi);
      }
    });

  const { isLoading: isRevokeTokenOauth2Loading, mutate: revokeTokenOAuth2Mutation } =
  useRevokeSearchConsoleAccountsTokenOAuth2Mutation(searchConsoleAccountId)({
    onSuccess: () => {
      appMessageApi.success('Токен OAuth2 успішно відкликано!');

      refetchSearchConsoleAccount();
    },
    onError: (error) => {
      messageOnError(error, appMessageApi);
    }
  });

  const handleDeleteSearchConsoleAccount = useCallback((): void => {
    deleteSearchConsoleAccountMutation();
  }, []);

  const handleRevokeTokenOAuth2 = useCallback((): void => {
    revokeTokenOAuth2Mutation();
  }, []);

  const handleEditSearchConsoleAccount = useCallback((): void => {
    navigate(AppRoutes.DomainsApiSearchConsoleAccountsEdit.replace(':searchConsoleAccountId', searchConsoleAccountId));
  }, [searchConsoleAccountId]);

  return (
    <FallbackedLayout
      isLoading={ isSearchConsoleAccountFetching }
      isError={ isSearchConsoleAccountError }
      error={ searchConsoleAccountError }
      data={ searchConsoleAccountData }
    >
      { (searchConsoleAccount): ReactElement => (
        <>
          <SearchConsoleAccountsOverviewView
            searchConsoleAccount={ searchConsoleAccount }
            isDeleteSearchConsoleAccountLoading={ isDeleteSearchConsoleAccountLoading }
            isItemsDisabled={ isDeleteSearchConsoleAccountLoading || isRevokeTokenOauth2Loading }
            handleRevokeTokenOAuth2={ handleRevokeTokenOAuth2 }
            handleDeleteSearchConsoleAccount={ handleDeleteSearchConsoleAccount }
            handleEditSearchConsoleAccount={ handleEditSearchConsoleAccount }
          />
        </>
      ) }
    </FallbackedLayout>
  );
});
